/**
 * Product Filters Hook
 *
 * Provides dynamic filter options for the shop.
 * Currently uses static data but designed to be easily switched to API-driven filters
 * when the aggregation backend is ready.
 */

import { useQuery } from '@tanstack/react-query';

export interface Brand {
  id: string;
  name: string;
  count?: number;
}

export interface Category {
  id: string;
  name: string;
  count?: number;
  parentId?: string;
}

export interface Vendor {
  id: string;
  name: string;
  available: boolean;
}

export interface FilterOptions {
  brands: Brand[];
  categories: Category[];
  vendors: Vendor[];
  priceRange: { min: number; max: number };
  isLoading: boolean;
}

// Static filter data - will be replaced with API calls when aggregation backend is ready
const STATIC_BRANDS: Brand[] = [
  { id: 'dell', name: 'Dell' },
  { id: 'hp', name: 'HP' },
  { id: 'lenovo', name: 'Lenovo' },
  { id: 'microsoft', name: 'Microsoft' },
  { id: 'cisco', name: 'Cisco' },
  { id: 'apple', name: 'Apple' },
  { id: 'samsung', name: 'Samsung' },
  { id: 'asus', name: 'ASUS' },
  { id: 'acer', name: 'Acer' },
];

const STATIC_CATEGORIES: Category[] = [
  { id: 'laptops', name: 'Laptops' },
  { id: 'desktops', name: 'Desktops' },
  { id: 'monitors', name: 'Monitors' },
  { id: 'accessories', name: 'Accessories' },
  { id: 'networking', name: 'Networking' },
  { id: 'storage', name: 'Storage' },
  { id: 'software', name: 'Software' },
  { id: 'printers', name: 'Printers' },
  { id: 'peripherals', name: 'Peripherals' },
];

const STATIC_VENDORS: Vendor[] = [
  { id: 'axiz', name: 'Axiz', available: true },
  // Future vendors will be added here
  // { id: 'takealot', name: 'Takealot', available: false },
  // { id: 'supplier-direct', name: 'Direct Suppliers', available: false },
];

/**
 * Hook to get available filter options
 */
export function useFilterOptions(): FilterOptions {
  // In the future, this will fetch from the aggregation API
  // const { data, isLoading } = useQuery({
  //   queryKey: ['filter-facets'],
  //   queryFn: () => api.get('/products/facets'),
  //   staleTime: 5 * 60 * 1000,
  // });

  return {
    brands: STATIC_BRANDS,
    categories: STATIC_CATEGORIES,
    vendors: STATIC_VENDORS,
    priceRange: { min: 0, max: 100000 },
    isLoading: false,
  };
}

/**
 * Hook to get brands
 */
export function useBrands() {
  return useQuery({
    queryKey: ['brands'],
    queryFn: async () => STATIC_BRANDS,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to get categories
 */
export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => STATIC_CATEGORIES,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to get vendors
 */
export function useVendors() {
  return useQuery({
    queryKey: ['vendors'],
    queryFn: async () => STATIC_VENDORS,
    staleTime: 10 * 60 * 1000,
  });
}
